import { localStorage as safeLocalStorage, sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import dayjs from 'dayjs';
import Api from '../../../../api';
import { HygienistProps, JobProps, PartialOffer } from '../../../../utils/types';
import { getHygienistDateDisplayInfo, HygienistDisplayInfo } from '../HygienistHome.utils';
import { parseBlockedUntilMs } from '../../../../utils/membership';
import { getTalentPaymentViewState } from '../../../../utils/talentPaymentState';
import { getTalentNonPaymentState } from '../../../../utils/talentInvoiceTiming';
import { useLocalDayClock } from '../../../../hooks/useLocalDayClock';
import { isPhoneNotVerifiedError, onPhoneVerified, phoneGateCalendarDates } from '../../../../utils/phoneVerificationGate';

type DateKey = string; // YYYY-MM-DD

/** Dispatched after hide-shift (etc.) so the home calendar refetches available jobs. */
export const HYGIENIST_CALENDAR_REFRESH_EVENT = 'ts:hygienist-calendar-refresh';

export function requestHygienistCalendarRefresh(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(HYGIENIST_CALENDAR_REFRESH_EVENT));
  }
}

const isHiddenShift = (shift: JobProps): boolean => !!(shift as JobProps & { isHided?: boolean }).isHided;

export const shouldShowHygienistCalendarJobAction = (j: any, now = dayjs()): boolean => {
  const isCompleted = Number(j?.status) === 4;
  if (!isCompleted) return false;

  const invoiceLater = !!j?.invoiceLater;
  const hasInvoiceId = !!j?.invoiceId || !!j?.invoice?.id;
  if (!hasInvoiceId && !invoiceLater) return true;
  if (!hasInvoiceId) return false;

  const reporting = getTalentNonPaymentState(j?.invoice, j?.paymentTerms, j?.location?.isCentralBooked, now);
  // This is a general action badge: payout troubleshooting remains actionable
  // even when an office non-payment report is excluded or already submitted.
  return getTalentPaymentViewState(j?.invoice).requiresAction || reporting.canReport;
};

interface UseHygienistCalendarDataReturn {
  isLoading: boolean;
  error: string | null;
  /** Flat array of all hygienist jobs (shared — avoids redundant refetches) */
  jobs: JobProps[];
  /** Returns a display info object for MonthView's getDisplayInfo prop */
  getDisplayInfo: (date: Date, jobs?: any[]) => HygienistDisplayInfo;
  /** Direct access to computed class by date for click handling */
  getClassForDate: (dateKey: DateKey) => string;
  /** For future: access to job ids per date */
  getJobsByDate: (dateKey: DateKey) => JobProps[];
    /** Full jobs-by-date map accessor to avoid redundant refetches */
    getJobsByDateMap: () => Record<DateKey, JobProps[]>;
  /** Access to offers grouped by date */
  getOffersByDate: (dateKey: DateKey) => PartialOffer[];
  /** Map of available shifts count per date */
  getAvailableByDate: () => Record<DateKey, number>;
  /** T2-5770: Map of active (status 0) PartialOffer count per date for the yellow indicator */
  getActiveOfferCountByDate: () => Record<DateKey, number>;
  /** Total count of available shifts (filtered) */
  availableShiftsCount: number;
  /** Blocked until timestamp for hygienist (ms since epoch) */
  getBlockedUntil: () => number | null;
  refreshAll: () => Promise<void>;
  /** T2-7517: the server refused shift data until the talent verifies their phone. */
  phoneVerificationRequired: boolean;
  /** Bumps when underlying calendar data (jobs/offers/available) is refreshed */
  dataVersion: number;
  /** Dates where hygienist is an InstaBook candidate (pending DO booking) */
  isInstabookCandidateDate: (dateKey: DateKey) => boolean;
  /** Cached shifts for a date (from the calendar load — avoids re-fetch on day tap) */
  getAvailableShiftsForDate: (dateKey: DateKey) => JobProps[];
  /** T2-7436: the talent's own shift, offer or InstaBook request (every job counts before the first load) */
  isRelevantJob: (jobId: number) => boolean;
  /** T2-7436: a shift that is only in the talent's available list */
  isAvailableOnlyJob: (jobId: number) => boolean;
  /** T2-7436: takes a shift someone else got off the calendar without a refetch */
  dropAvailableJob: (jobId: number) => void;
}

/** The lists from the last calendar load. */
type CalendarSource = {
  hygienist: HygienistProps | null;
  jobs: JobProps[];
  offers: PartialOffer[];
  offersByDate: Record<DateKey, PartialOffer[]>;
  available: JobProps[];
};

type AvailableCalendarSlice = {
  /** Offerable shift counts (after cooldown) — drives navigation + green badges */
  availableMap: Record<DateKey, number>;
  shiftsByDate: Record<DateKey, JobProps[]>;
  /** Cooldown-blocked shift counts — display-only white badges + calendar-blocked */
  penaltyBlockedMap: Record<DateKey, number>;
  filteredCount: number;
  instabookCandidateByDate: Record<DateKey, boolean>;
};

function isShiftBlockedByCooldown(startDateKey: DateKey, blockedUntilMs: number | null): boolean {
  if (!blockedUntilMs) return false;
  return dayjs(startDateKey).isSameOrBefore(dayjs(blockedUntilMs), 'day');
}

/** Daily Auto-book schedule dates from hygienist state + persisted user snapshot. */
function readDailyAutobookDateKeys(hyg: HygienistProps | null): Record<DateKey, boolean> {
  const keys: Record<DateKey, boolean> = {};
  const addFromDaily = (daily: Array<{ startDate?: string }> | undefined) => {
    (daily || []).forEach((entry) => {
      const key = typeof entry?.startDate === 'string' ? entry.startDate.slice(0, 10) : '';
      if (key) keys[key] = true;
    });
  };
  addFromDaily((hyg as any)?.DailySettings);
  try {
    const raw = safeLocalStorage.getItem('userAccount');
    if (raw) {
      const user = JSON.parse(raw);
      addFromDaily(user?.hygienist?.DailySettings);
    }
  } catch {}
  return keys;
}

/**
 * T2-7517: while the server holds shifts back for an unverified phone, Home's
 * calendar gets only the days that have shifts. Each day becomes one placeholder
 * with nothing but its date (no id, office or times), so it paints green and a
 * tap asks for verification. Code that reads more than startDate must not run
 * while phoneVerificationRequired is set.
 */
export function lockedCalendarDays(error: unknown): JobProps[] {
  return phoneGateCalendarDates(error).map(startDate => ({ startDate } as unknown as JobProps));
}

/** Build per-date counts + shift lists with legacy exclusion parity */
export function buildAvailableCalendarSlice(
  available: JobProps[],
  fetchedJobs: JobProps[],
  fetchedOffers: PartialOffer[],
  blockedUntilMs: number | null = null,
): AvailableCalendarSlice {
  const bookedOrCompletedByDate: Record<DateKey, boolean> = {};
  (fetchedJobs || []).forEach((j) => {
    if (j && (Number(j.status) === 3 || Number(j.status) === 4)) {
      const key = (j.startDate || '') as DateKey;
      bookedOrCompletedByDate[key] = true;
    }
  });
  const hasOfferForJobId: Record<number, boolean> = {};
  (fetchedOffers || []).forEach((off) => {
    if (Number.isInteger(off?.jobId)) hasOfferForJobId[off.jobId] = true;
  });
  const instabookedByDate: Record<DateKey, boolean> = {};
  (fetchedJobs || []).forEach((j) => {
    const isInstabook = Number(j.status) === 2 && ((j as any).Instabook === 1 || (j as any).Autobook === 1);
    if (isInstabook) {
      const key = (j.startDate || '') as DateKey;
      instabookedByDate[key] = true;
    }
  });

  const instabookCandidateByDate: Record<DateKey, boolean> = {};
  (available || []).forEach((shift) => {
    if (isHiddenShift(shift)) return;
    if ((shift as any).isInstabookCandidate) {
      const key = (shift.startDate || '') as DateKey;
      if (key) instabookCandidateByDate[key] = true;
    }
  });

  const availableMap: Record<DateKey, number> = {};
  const penaltyBlockedMap: Record<DateKey, number> = {};
  const shiftsByDate: Record<DateKey, JobProps[]> = {};
  let filteredCount = 0;
  (available || []).forEach((shift) => {
    const key = (shift.startDate || '') as DateKey;
    if (!key) return;
    if (isHiddenShift(shift)) return;
    const excluded = bookedOrCompletedByDate[key]
      || !!hasOfferForJobId[(shift as any)?.id]
      || instabookedByDate[key];
    if (excluded) return;

    if (isShiftBlockedByCooldown(key, blockedUntilMs)) {
      penaltyBlockedMap[key] = (penaltyBlockedMap[key] || 0) + 1;
      return;
    }

    availableMap[key] = (availableMap[key] || 0) + 1;
    if (!shiftsByDate[key]) shiftsByDate[key] = [];
    shiftsByDate[key].push(shift);
    filteredCount++;
  });

  return { availableMap, shiftsByDate, penaltyBlockedMap, filteredCount, instabookCandidateByDate };
}

/**
 * T2-7436: jobs whose live updates refresh Home: the talent's own shifts, the
 * jobs they made offers on and the shifts they asked to InstaBook.
 */
export function collectRelevantJobIds(
  jobs: JobProps[],
  offers: PartialOffer[],
  available: JobProps[],
): Set<number> {
  const ids = new Set<number>();
  const add = (id: unknown) => {
    const jobId = Number(id);
    if (Number.isInteger(jobId) && jobId > 0) ids.add(jobId);
  };
  (jobs || []).forEach((job) => add(job?.id));
  (offers || []).forEach((offer) => add(offer?.jobId));
  (available || []).forEach((shift) => {
    if ((shift as any)?.isInstabookCandidate) add(shift?.id);
  });
  return ids;
}

/** Canadian assistants who agreed to admin work also see Admin/Reception (type 2) shifts. */
export function talentSeesAdminShifts(hyg: HygienistProps | null | undefined): boolean {
  if (!hyg || hyg.type !== 1 || hyg.country !== 0) return false;
  if (hyg.agreedAdmin) {
    return new Date(hyg.agreedAdmin).getFullYear() > 1970;
  }
  try { return safeSessionStorage.getItem('ts_admin_agreed') === '1'; } catch { return false; }
}

/** Utility */
const formatDateKey = (d: Date): DateKey => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Compute YYYY-M-D (month zero based) for badge hooks */
const formatLegacyDataDate = (d: Date): string => {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

export const useHygienistCalendarData = (): UseHygienistCalendarDataReturn => {
  const invoiceClock = useLocalDayClock();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [phoneVerificationRequired, setPhoneVerificationRequired] = useState<boolean>(false);
  const [hygienist, setHygienist] = useState<HygienistProps | null>(null);
  const [jobs, setJobs] = useState<JobProps[]>([]);
  const [availableJobsByDate, setAvailableJobsByDate] = useState<Record<DateKey, number>>({});
  const [penaltyBlockedByDate, setPenaltyBlockedByDate] = useState<Record<DateKey, number>>({});
  const [classByDate, setClassByDate] = useState<Record<DateKey, string>>({});
  const [jobsByDate, setJobsByDate] = useState<Record<DateKey, JobProps[]>>({});
  const [offersByDate, setOffersByDate] = useState<Record<DateKey, PartialOffer[]>>({});
  const [availableShiftsCount, setAvailableShiftsCount] = useState<number>(0);
  const [instabookCandidateDates, setInstabookCandidateDates] = useState<Record<DateKey, boolean>>({});
  const [availableShiftsByDate, setAvailableShiftsByDate] = useState<Record<DateKey, JobProps[]>>({});
  const [dataVersion, setDataVersion] = useState<number>(0);
  const hasShownCalendarRef = useRef(false);
  // T2-7436: kept so live updates can tell which jobs concern this talent and
  // drop a shift someone else took without a refetch.
  const sourceRef = useRef<CalendarSource | null>(null);
  const relevantJobIdsRef = useRef<Set<number>>(new Set());

  const computeClasses = useCallback((
    inputJobs: JobProps[],
    inputAvailableByDate: Record<DateKey, number>,
    inputOffersByDate: Record<DateKey, PartialOffer[]>,
    _blockedUntil: number | null,
    inputInstabookCandidateByDate: Record<DateKey, boolean> = {},
    inputPenaltyBlockedByDate: Record<DateKey, number> = {},
  ) => {
    const nextClassByDate: Record<DateKey, string> = {};
    const nextJobsByDate: Record<DateKey, JobProps[]> = {};

    // Group jobs by date (excluding past dates from indicator rendering)
    inputJobs.forEach((job) => {
      const dateKey = job.startDate as DateKey;
      if (!nextJobsByDate[dateKey]) nextJobsByDate[dateKey] = [];
      nextJobsByDate[dateKey].push(job);
    });

    // Decide class priority per date, incorporating offers combinations
    const combinedKeys = { ...nextJobsByDate } as Record<DateKey, any>;
    Object.keys(inputAvailableByDate).forEach((k) => { if (!combinedKeys[k]) combinedKeys[k] = null; });
    Object.keys(inputOffersByDate).forEach((k) => { if (!combinedKeys[k]) combinedKeys[k] = null; });
    Object.keys(inputInstabookCandidateByDate).forEach((k) => { if (!combinedKeys[k]) combinedKeys[k] = null; });
    Object.keys(inputPenaltyBlockedByDate).forEach((k) => { if (!combinedKeys[k]) combinedKeys[k] = null; });

    Object.keys(combinedKeys).forEach((dateKey) => {
      const list = nextJobsByDate[dateKey] || [];
      const hasCompleted = list.some((j) => j.status === 4);
      const hasBooked = list.some((j) => j.status === 3);
      // Offer-based classes from PartialOffer mapping: 0=Custom, 3=Expired
      const offers = inputOffersByDate[dateKey] || [];
      const offerStatuses = offers.map((o: any) => Number(o?.status)).filter((s) => s === 0 || s === 3);
      const hasCustom = offerStatuses.includes(0);
      const hasExpired = offerStatuses.includes(3);
      const hasInstabook = list.some((j) => j.Instabook === 1 || ((j as any).Autobook === 1 && Number(j.status) === 2));
      const hasInstabookCandidate = !!inputInstabookCandidateByDate[dateKey];
      const hasAvailable = (inputAvailableByDate[dateKey] || 0) > 0;
      const hasPenaltyBlocked = (inputPenaltyBlockedByDate[dateKey] || 0) > 0;

      let comboClass = '';
      if (offerStatuses.length >= 2) {
        // Determine combination classes using first two offers (legacy parity heuristic)
        const first = offerStatuses[0];
        const second = offerStatuses[1];
        if (first === 0 && second === 0) comboClass = 'calendar-custom-custom';
        else if (first === 0 && second === 3) comboClass = 'calendar-custom-expired';
        else if (first === 3 && second === 0) comboClass = 'calendar-expired-custom';
        else if (first === 3 && second === 3) comboClass = 'calendar-expired-expired';
      }
      // T2-6023: An all-expired offer combo (e.g. 'calendar-expired-expired')
      // should NOT grey out the day when the talent still has other available
      // shifts on that date to view/offer on. This mirrors the single-expired
      // handling (T2-5850) but for the multi-offer case below.
      const isAllExpiredCombo = comboClass === 'calendar-expired-expired';
      if (isAllExpiredCombo && hasAvailable) {
        comboClass = '';
      }
      /** T2-5797: Auto-book interest alone should not paint the day yellow like a custom offer */
      const instabookCandidateWithoutCustomOffer =
        hasInstabookCandidate && !hasCustom && !hasExpired && !comboClass;

      let cssClass = '';
      if (hasCompleted) cssClass = 'calendar-completed';
      else if (hasBooked) cssClass = 'calendar-booked';
      // T2-5832 / T2-6054: mirror DO split-day visual when a date has BOTH an
      // active offer (custom) and at least one other available shift. This
      // must beat the multi-offer combo class (calendar-custom-custom etc.)
      // below, otherwise a date with 2+ active offers + an available shift
      // renders as solid yellow instead of the green/yellow diagonal split.
      else if (hasCustom && hasAvailable) cssClass = 'calendar-custom-available';
      else if (comboClass) cssClass = comboClass;
      else if (hasCustom && hasExpired) cssClass = 'calendar-custom-expired';
      else if (hasCustom) cssClass = 'calendar-custom';
      // T2-5850: an expired offer should NOT grey out the day when the talent
      // still has other available shifts to view on that date. Only render as
      // plain "expired" (grey) when the expired offer is the only thing on the
      // calendar for that day. When there is an expired offer + other
      // available shifts we fall through to `calendar-available` so the cell
      // stays green (the grey "expired" corner indicator continues to render
      // via expiredOfferCount below).
      else if (hasExpired && !hasAvailable) cssClass = 'calendar-expired';
      else if (hasInstabook) cssClass = 'calendar-instabooked';
      else if (instabookCandidateWithoutCustomOffer) cssClass = 'calendar-available';
      else if (hasInstabookCandidate) cssClass = 'calendar-custom';
      else if (hasAvailable) cssClass = 'calendar-available';
      else if (hasPenaltyBlocked) cssClass = 'calendar-blocked';
      // Edge-case: only an expired offer exists and it isn't paired with
      // availability — retain legacy grey class for compatibility.
      else if (hasExpired) cssClass = 'calendar-expired';

      nextClassByDate[dateKey] = cssClass;
    });

    setJobsByDate(nextJobsByDate);
    setClassByDate(nextClassByDate);
  }, []);

  const applyCalendarSource = useCallback((source: CalendarSource) => {
    const blockedUntilMs = parseBlockedUntilMs(source.hygienist?.blockedUntil);
    const {
      availableMap,
      shiftsByDate,
      penaltyBlockedMap,
      filteredCount,
      instabookCandidateByDate,
    } = buildAvailableCalendarSlice(source.available, source.jobs, source.offers, blockedUntilMs);

    setInstabookCandidateDates(instabookCandidateByDate);
    setAvailableJobsByDate(availableMap);
    setPenaltyBlockedByDate(penaltyBlockedMap);
    setAvailableShiftsByDate(shiftsByDate);
    setAvailableShiftsCount(filteredCount);
    computeClasses(
      source.jobs,
      availableMap,
      source.offersByDate,
      blockedUntilMs,
      instabookCandidateByDate,
      penaltyBlockedMap,
    );
    setDataVersion((v) => v + 1);
  }, [computeClasses]);

  const loadAll = useCallback(async () => {
    const showBlockingLoader = !hasShownCalendarRef.current;
    try {
      if (showBlockingLoader) {
        setIsLoading(true);
        setError(null);
      }

      // Start jobs/offers immediately; available jobs starts as soon as user (type) is known.
      const userPromise = Api.getUser();
      const jobsPromise = Api.getHygienistJobs('') as Promise<JobProps[]>;
      const offersPromise = Api.getHygienistOffers() as Promise<PartialOffer[]>;

      const user = await userPromise;
      const hyg = user?.hygienist || null;
      const type = hyg?.type ?? 0;
      const includeAdmin = talentSeesAdminShifts(hyg);

      // T2-7517: a "phone not verified" answer must not take the talent's own
      // bookings and offers down with it. The calendar shows the days that have
      // shifts (the only thing the server sends), without counts, and a tap asks
      // for verification (Holly, 2026-10-09). The flag changes in the same
      // render as the shift count (below), so Home never sees "verified" next to
      // the old count.
      let shiftsRefused = false;
      const availablePromise = Api.getAvailableJobs(type, undefined, undefined, includeAdmin, { calendar: true, background: true })
        .catch((e: unknown) => {
          if (!isPhoneNotVerifiedError(e)) throw e;
          shiftsRefused = true;
          return lockedCalendarDays(e);
        });
      const [fetchedJobs, fetchedOffers, available] = await Promise.all([
        jobsPromise,
        offersPromise,
        availablePromise,
      ]);

      setHygienist(hyg);
      setJobs(fetchedJobs || []);

      const nextOffersByDate: Record<DateKey, PartialOffer[]> = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      (fetchedOffers || []).forEach((offer) => {
        const dateKey = offer?.job?.startDate as DateKey;
        if (!dateKey) return;
        const offerDate = new Date(dateKey + 'T00:00:00');
        const isExpiredOffer = Number(offer?.status) === 3;
        // Past dates: keep expired offers so the calendar can grey the day and
        // tapping still opens Expired Custom Offer details (T2-6024 / T2-6355).
        if (offerDate < today && !isExpiredOffer) return;
        if (!nextOffersByDate[dateKey]) nextOffersByDate[dateKey] = [];
        nextOffersByDate[dateKey].push(offer);
      });
      setOffersByDate(nextOffersByDate);

      const source: CalendarSource = {
        hygienist: hyg,
        jobs: fetchedJobs || [],
        offers: fetchedOffers || [],
        offersByDate: nextOffersByDate,
        available: available || [],
      };
      relevantJobIdsRef.current = collectRelevantJobIds(source.jobs, source.offers, source.available);
      sourceRef.current = source;
      // The app mounts with ReactDOM.render (legacy root), which renders each
      // update made after an await on its own; batch these two.
      unstable_batchedUpdates(() => {
        setPhoneVerificationRequired(shiftsRefused);
        applyCalendarSource(source);
      });
    } catch (e) {
      console.error('Failed to load hygienist calendar data', e);
      setError('Failed to load calendar');
    } finally {
      if (showBlockingLoader) {
        setIsLoading(false);
        hasShownCalendarRef.current = true;
      }
    }
  }, [applyCalendarSource]);

  // T2-7517: shifts appear as soon as the talent verifies, without signing out.
  useEffect(() => onPhoneVerified(() => { loadAll(); }), [loadAll]);

  useEffect(() => {
    loadAll();
  }, [loadAll, invoiceClock]);

  useEffect(() => {
    const onRefresh = () => { void loadAll(); };
    window.addEventListener(HYGIENIST_CALENDAR_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(HYGIENIST_CALENDAR_REFRESH_EVENT, onRefresh);
  }, [loadAll]);

  // Expose function for MonthView
  const getDisplayInfo = useCallback((date: Date): HygienistDisplayInfo => {
    const dateKey = formatDateKey(date);
    const cssClass = classByDate[dateKey] || '';
    const jobsForDate = jobsByDate[dateKey] as any[] || [];
    const base = getHygienistDateDisplayInfo(date, jobsForDate as any);

    const hasAction = (jobsForDate || []).some((j: any) => (
      shouldShowHygienistCalendarJobAction(j, dayjs(invoiceClock))
    ));

    const offersForDate = offersByDate[dateKey] || [];
    const offerableCount = availableJobsByDate[dateKey] || 0;
    const penaltyBlockedCount = penaltyBlockedByDate[dateKey] || 0;
    const isPenaltyBlocked = penaltyBlockedCount > 0;
    const badgeCount = isPenaltyBlocked ? penaltyBlockedCount : offerableCount;
    // T2-5841: once a date is booked or completed, the talent should see only
    // that state on the calendar — all other offer/expired overlays are
    // suppressed to match the DO-side parity and avoid confusing users.
    const isBookedOrCompleted = cssClass === 'calendar-booked' || cssClass === 'calendar-completed';
    const activeOfferCount = isBookedOrCompleted
      ? 0
      : offersForDate.reduce(
          (acc: number, offer: PartialOffer) => (Number(offer.status) === 0 ? acc + 1 : acc),
          0,
        );
    const autobookDates = readDailyAutobookDateKeys(hygienist);
    const showAutobookSchedule = !!autobookDates[dateKey] && !base.isPast;
    const expiredOfferCount = isBookedOrCompleted
      ? 0
      : offersForDate.reduce(
          (acc: number, offer: PartialOffer) => (Number(offer.status) === 3 ? acc + 1 : acc),
          0,
        );
    const hasExpiredOffer = expiredOfferCount > 0;

    const indicatorAllowedByClass = cssClass
      && cssClass !== 'calendar-available'
      && cssClass !== 'calendar-blocked';
    // T2-6023: When an expired-offer date repaints as `calendar-available`
    // (because the talent still has other available shifts on that day —
    // single-offer case T2-5850, or multi-offer expired-expired case fixed
    // above), the cssClass-gated `indicatorAllowedByClass` becomes false
    // and the corner "expired" count badge would otherwise disappear.
    // The badge should follow the data (expiredOfferCount > 0), not the
    // square color, so the talent can still see how many expired offers
    // are sitting on that date.
    const shouldShowIndicators = (!!base.shouldShowIndicators)
      || (!!indicatorAllowedByClass)
      || (expiredOfferCount > 0);
    return {
      ...base,
      statusClass: cssClass,
      hasAction,
      shouldShowIndicators,
      hasExpiredOffer,
      expiredOfferCount,
      // While the phone gate holds shifts back, the calendar knows days, not counts.
      availableShiftCount: badgeCount > 0 && !phoneVerificationRequired ? badgeCount : undefined,
      isPenaltyBlocked: isPenaltyBlocked || undefined,
      activeOfferCount: activeOfferCount > 0 ? activeOfferCount : undefined,
      showAutobookSchedule: showAutobookSchedule || undefined,
    };
  }, [classByDate, jobsByDate, offersByDate, availableJobsByDate, penaltyBlockedByDate, hygienist, invoiceClock, phoneVerificationRequired]);

  const getClassForDate = useCallback((dateKey: DateKey) => classByDate[dateKey] || '', [classByDate]);
  const getJobsForDate = useCallback((dateKey: DateKey) => jobsByDate[dateKey] || [], [jobsByDate]);
  const getOffersForDate = useCallback((dateKey: DateKey) => offersByDate[dateKey] || [], [offersByDate]);
  const getAvailableByDateCb = useCallback(() => ({ ...availableJobsByDate }), [availableJobsByDate]);
  const getActiveOfferCountByDateCb = useCallback(() => {
    const map: Record<DateKey, number> = {};
    Object.entries(offersByDate).forEach(([dateKey, list]) => {
      const active = (list || []).filter((o: any) => Number(o?.status) === 0).length;
      if (active > 0) map[dateKey] = active;
    });
    return map;
  }, [offersByDate]);
  const getBlockedUntilCb = useCallback(() => {
    const fromState = parseBlockedUntilMs(hygienist?.blockedUntil);
    if (fromState != null) return fromState;
    const windowHyg = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
    return parseBlockedUntilMs(windowHyg?.blockedUntil);
  }, [hygienist]);
  const getJobsByDateMapCb = useCallback(() => ({ ...jobsByDate }), [jobsByDate]);
  const isInstabookCandidateDateCb = useCallback((dateKey: DateKey) => !!instabookCandidateDates[dateKey], [instabookCandidateDates]);
  const getAvailableShiftsForDateCb = useCallback(
    (dateKey: DateKey) => availableShiftsByDate[dateKey] || [],
    [availableShiftsByDate],
  );
  const isRelevantJob = useCallback((jobId: number) => (
    !sourceRef.current || relevantJobIdsRef.current.has(jobId)
  ), []);
  const isAvailableOnlyJob = useCallback((jobId: number) => {
    const source = sourceRef.current;
    if (!source || relevantJobIdsRef.current.has(jobId)) return false;
    return source.available.some((shift) => Number(shift?.id) === jobId);
  }, []);
  const dropAvailableJob = useCallback((jobId: number) => {
    const source = sourceRef.current;
    if (!source) return;
    const available = source.available.filter((shift) => Number(shift?.id) !== jobId);
    if (available.length === source.available.length) return;
    sourceRef.current = { ...source, available };
    applyCalendarSource(sourceRef.current);
  }, [applyCalendarSource]);

  return {
    isLoading,
    error,
    jobs,
    getDisplayInfo,
    getClassForDate,
    getJobsByDate: getJobsForDate,
    getJobsByDateMap: getJobsByDateMapCb,
    getOffersByDate: getOffersForDate,
    getAvailableByDate: getAvailableByDateCb,
    getBlockedUntil: getBlockedUntilCb,
    refreshAll: loadAll,
    phoneVerificationRequired,
    availableShiftsCount,
    dataVersion,
    isInstabookCandidateDate: isInstabookCandidateDateCb,
    getActiveOfferCountByDate: getActiveOfferCountByDateCb,
    getAvailableShiftsForDate: getAvailableShiftsForDateCb,
    isRelevantJob,
    isAvailableOnlyJob,
    dropAvailableJob,
  };
};

export default useHygienistCalendarData;
